import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { ArrowLeft, Save, Loader2, Upload, FileText, Check } from "lucide-react";
import LeadFileActions from "../components/LeadFileActions";

/**
 * Client Upload — port of Kinntegra-Old `client-upload`.
 * Documents lifted from the Angular template:
 *   AOF, FATCA, Letter of Engagement (LOE), Mandate (per mandate)
 *   FATCA Uploaded to BSE (flag)
 *
 * MOCKED file storage: files are stored as base64 references on the lead doc.
 * Wire Emergent Object Storage for production uploads (see PRD.md backlog).
 */
const REQUIRED_DOCS = [
  { key: "aof",   label: "Account Opening Form (AOF)", note: "Do not include cheque copy" },
  { key: "fatca", label: "FATCA Declaration" },
  { key: "loe",   label: "Letter of Engagement" },
];

export default function LeadUploadPage() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [docs, setDocs] = useState({});
  const [fatcaOnBse, setFatcaOnBse] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/lead-mgmt/${leadId}`);
        setLead(data.data);
        const up = data.data?.uploads || {};
        setDocs(up.docs || {});
        setFatcaOnBse(!!up.fatca_on_bse);
      } catch (e) {
        toast.error("Failed to load lead: " + (e?.response?.data?.detail || e.message));
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line
  }, [leadId]);

  const onPick = async (key, file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("File must be under 5MB");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/lead-mgmt/${lead.id}/files?slot=${encodeURIComponent(key)}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDocs(prev => ({ ...prev, [key]: data.data }));
      toast.success(`${file.name} uploaded`);
    } catch (e) {
      toast.error("Upload failed: " + (e?.response?.data?.detail || e.message));
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/lead-mgmt/${lead.id}/uploads`, {
        uploads: { docs, fatca_on_bse: fatcaOnBse },
      });
      toast.success("Documents saved");
    } catch (e) {
      toast.error("Save failed: " + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  if (loading) return <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-teal-600" size={32}/></div>;
  if (!lead) return <div className="p-10 text-center text-gray-400">Lead not found.</div>;

  const mandates = lead.mandates || [];

  return (
    <div className="px-4 py-6" data-testid="lead-upload-page">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(`/leads/${leadId}/mandate`)}
                  className="rounded-md border border-gray-300 bg-white px-2.5 py-1.5 hover:bg-gray-100"
                  data-testid="upload-back-btn"><ArrowLeft size={16}/></button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Documents Upload</h1>
            <p className="text-xs text-gray-500 mt-0.5">Attach AOF, FATCA, LOE and mandate scans — {lead.first_name} {lead.last_name}</p>
          </div>
        </div>
      </div>

      <Card className="border-gray-200 shadow-sm">
        <CardContent className="p-6">
          <label className="text-sm font-medium text-gray-700 flex items-center gap-2 mb-4">
            <Upload size={15}/> Required Documents
          </label>

          <div className="space-y-3 mb-6">
            {REQUIRED_DOCS.map(d => {
              const attached = docs[d.key];
              return (
                <div key={d.key} className="flex items-center justify-between border border-gray-200 rounded-lg p-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-800">{d.label}</div>
                    {d.note && <div className="text-[11px] text-gray-500 mt-0.5">{d.note}</div>}
                    {attached && (
                      <div className="text-xs text-emerald-700 mt-1 flex items-center gap-1">
                        <Check size={12}/> {attached.name} ({Math.round(attached.size / 1024)} KB)
                        <LeadFileActions file={attached} api={api} leadId={leadId} testid={`upload-${d.key}`}/>
                      </div>
                    )}
                  </div>
                  <label className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium hover:bg-gray-50 cursor-pointer"
                         data-testid={`upload-${d.key}-btn`}>
                    <Upload size={12}/> {attached ? "Replace" : "Upload"}
                    <input type="file" hidden accept=".pdf,.jpg,.jpeg,.png"
                           onChange={e => onPick(d.key, e.target.files?.[0])}/>
                  </label>
                </div>
              );
            })}
          </div>

          {mandates.length > 0 && (
            <>
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2 mb-3">
                <FileText size={15}/> Mandate Scans ({mandates.length})
              </label>
              <div className="space-y-3 mb-6">
                {mandates.map((m, idx) => {
                  const key = `mandate_${m.id || idx}`;
                  const attached = docs[key];
                  return (
                    <div key={key} className="flex items-center justify-between border border-gray-200 rounded-lg p-3">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-gray-800">Mandate {idx + 1} — ₹{m.amount || 0} · {m.mandate_type}</div>
                        {attached && (
                          <div className="text-xs text-emerald-700 mt-1 flex items-center gap-1">
                            <Check size={12}/> {attached.name}
                            <LeadFileActions file={attached} api={api} leadId={leadId} testid={`upload-mandate-${idx}`}/>
                          </div>
                        )}
                      </div>
                      <label className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium hover:bg-gray-50 cursor-pointer"
                             data-testid={`upload-mandate-${idx}-btn`}>
                        <Upload size={12}/> {attached ? "Replace" : "Upload"}
                        <input type="file" hidden accept=".pdf,.jpg,.jpeg,.png"
                               onChange={e => onPick(key, e.target.files?.[0])}/>
                      </label>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          <label className="inline-flex items-center gap-2 text-sm text-gray-700 mb-4">
            <input type="checkbox" checked={fatcaOnBse}
                   onChange={e => setFatcaOnBse(e.target.checked)}
                   className="h-4 w-4 accent-[#1a7f6f]"
                   data-testid="upload-fatca-on-bse"/>
            FATCA already uploaded on BSE portal
          </label>

          <div className="flex items-center justify-end pt-4 border-t mt-2">
            <button onClick={save} disabled={busy}
                    className="inline-flex items-center gap-2 rounded-md bg-[#1a7f6f] hover:bg-[#166a5c] text-white text-sm font-semibold px-8 py-2 disabled:opacity-60"
                    data-testid="upload-save-btn">
              {busy ? <Loader2 className="animate-spin" size={14}/> : <Save size={14}/>} Save Documents
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
